import { LensPickerSheet } from "@/components/cart/LensPickerSheet";
import { useParams, useNavigate, Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { useCountry } from "@/hooks/useCountry";
import { useFrameDetail } from "@/hooks/useFrameDetail";
import { useSimilarFrames } from "@/hooks/useSimilarFrames";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Disclosure } from "@/components/ui/disclosure";
import {
  ArrowLeft, AlertCircle,
  Check, MapPin, ChevronRight, Store, ShoppingBag
} from "lucide-react";
import { useCartStore } from "@/stores/cartStore";

import { SchemaGraph } from "@/components/seo/SchemaGraph";
import { faqNode, breadcrumbNode, productNode, organizationNode, websiteNode } from "@/lib/schema/generators";
import { getLastListPath } from "@/lib/scrollMemory";
import { frameImageUrl } from "@/lib/frameImage";
import { getLapairePlusCopy } from "@/content/lapairePlus";
import FavoriteButton from "@/components/account/FavoriteButton";

const ImageLightbox = lazy(() => import("@/components/product/ImageLightbox").then(m => ({ default: m.ImageLightbox })));

function replacePlaceholders(text: string, vars: Record<string, string | number>) {
  let result = text;
  for (const [key, value] of Object.entries(vars)) {
    result = result.split(`{${key}}`).join(String(value));
  }
  return result;
}

// (lensSubtitle helper removed — lens picker is no longer shown on product page)


export default function ProductPage() {
  const { handle: slug, country: countrySlug } = useParams();
  const navigate = useNavigate();
  const { country, t, formatPrice, lang } = useCountry();
  const addCartItem = useCartStore((s) => s.addItem);

  const { frame, framePriceTtc, lensOptions, inStock, stockQuantity, isLoading: fetching, getFinalPrice } = useFrameDetail(slug, country.id);

  // Fetch cities for this country (used in "Local availability" block)
  // Deferred: only run once the main frame is loaded so it doesn't compete with the LCP image.
  const { data: cities } = useQuery({
    queryKey: ["cities", country.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("cities")
        .select("id, name, slug")
        .eq("country_id", country.id)
        .limit(5);
      return data || [];
    },
    enabled: !!country.id,
  });

  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedLensId, setSelectedLensId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (lensOptions.length > 0 && (!selectedLensId || !lensOptions.some((lens) => lens.id === selectedLensId))) {
      const cheapest = lensOptions.reduce((a, b) => (a.price_ttc < b.price_ttc ? a : b));
      setSelectedLensId(cheapest.id);
    }
  }, [lensOptions, selectedLensId]);

  // Dynamic price calculation
  const pricing = useMemo(() => {
    if (!selectedLensId) return null;
    return getFinalPrice(selectedLensId);
  }, [selectedLensId, getFinalPrice]);

  // Mobile Money actif dans ce pays ? (réassurance page produit uniquement)
  const { data: mobileMoneyEnabled } = useQuery({
    queryKey: ["country-pawapay", country.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("countries")
        .select("pawapay_enabled")
        .eq("id", country.id)
        .maybeSingle();
      return !!data?.pawapay_enabled;
    },
    enabled: !!country.id,
    staleTime: 10 * 60 * 1000,
  });

  const priceDisplay = useMemo(() => {
    if (pricing) return formatPrice(pricing.totalTtc);
    if (framePriceTtc !== null) return formatPrice(framePriceTtc);
    return "";
  }, [pricing, framePriceTtc, formatPrice]);

  const plusCopy = getLapairePlusCopy(lang);
  const countryName = country.name;

  // Build images array
  const images = useMemo(() => {
    if (!frame) return [];
    const imgs: { url: string; alt: string }[] = [];
    if (frame.base_image_url) imgs.push({ url: frame.base_image_url, alt: frame.name });
    if (frame.gallery) {
      frame.gallery.forEach((url) => {
        if (url && url !== frame.base_image_url) imgs.push({ url, alt: frame.name });
      });
    }
    return imgs;
  }, [frame]);

  // FAQ data
  const faqItems = useMemo(() => {
    const vars = { country: countryName, prep: country.preposition };
    return [
      { q: replacePlaceholders(t("product.faq1Q"), vars), a: replacePlaceholders(t("product.faq1A"), vars) },
      { q: replacePlaceholders(t("product.faq2Q"), vars), a: replacePlaceholders(t("product.faq2A"), vars) },
      { q: replacePlaceholders(t("product.faq3Q"), vars), a: replacePlaceholders(t("product.faq3A"), vars) },
      { q: replacePlaceholders(t("product.faq4Q"), vars), a: replacePlaceholders(t("product.faq4A"), vars) },
      { q: replacePlaceholders(t("product.faq5Q"), vars), a: replacePlaceholders(t("product.faq5A"), vars) },
    ];
  }, [t, countryName, country.preposition]);

  // Similar products: ciblé via `country_catalog_mv` (au lieu du catalogue pays entier).
  // On récupère un petit sous-ensemble filtré côté DB (même forme / couleur / collection),
  // puis on applique la cascade de tiers côté client pour choisir les 6 meilleurs.
  const { data: catalog } = useSimilarFrames(frame ?? undefined, frame ? country.id : undefined);
  const similarProducts = useMemo(() => {
    if (!frame || !catalog || catalog.length === 0) return [];
    const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
    const refColor = norm(frame.color) || norm(frame.color_en);
    const refShape = norm(frame.shape) || norm(frame.shape_en);
    const refCollection = norm(frame.collection_name);

    const others = catalog
      .filter((c) => c.id !== frame.id)
      .map((c) => ({
        frame: c,
        color: norm(c.color) || norm(c.color_en),
        shape: norm(c.shape) || norm(c.shape_en),
        collection: norm(c.collection_name),
      }));

    const tiers: Array<(c: typeof others[number]) => boolean> = [
      (c) => !!refCollection && !!refColor && !!refShape &&
              c.collection === refCollection && c.color === refColor && c.shape === refShape,
      (c) => !!refColor && !!refShape && c.color === refColor && c.shape === refShape,
      (c) => !!refShape && c.shape === refShape,
      (c) => !!refColor && c.color === refColor,
    ];

    for (const match of tiers) {
      const found = others.filter(match);
      if (found.length > 0) return found.slice(0, 6).map((c) => c.frame);
    }
    return [];
  }, [frame, catalog]);


  // Build schema nodes (rendered via SchemaGraph below — no useEffect needed)
  const schemaNodes = useMemo(() => {
    if (!frame) return [];
    const allImages = Array.from(
      new Set([frame.base_image_url, ...(frame.gallery ?? [])].filter(Boolean) as string[])
    );
    const frameColor = lang === "en" ? frame.color_en || frame.color : frame.color;
    const frameMaterial = lang === "en" ? frame.material_en || frame.material : frame.material;
    const frameSize = lang === "en" ? frame.size_label_en || frame.size_label : frame.size_label;
    const frameName = lang === "en" ? frame.name_en || frame.name : frame.name;
    const frameDesc = lang === "en" ? frame.description_en || frame.description : frame.description;

    const hasLensOptions = lensOptions.length > 0 && framePriceTtc !== null;
    const highPrice = hasLensOptions && framePriceTtc !== null
      ? framePriceTtc + Math.max(...lensOptions.map((l) => l.price_ttc))
      : null;

    return [
      organizationNode({ lang, countryName }),
      websiteNode(lang),
      productNode({
        url: `/${countrySlug}/product/${slug}`,
        name: frameName,
        description: frameDesc ?? `${frameName} – Lapaire ${countryName}`,
        ...(allImages.length > 0 ? { image: allImages } : {}),
        sku: frame.sku,
        ...(frameColor ? { color: frameColor } : {}),
        ...(frameMaterial ? { material: frameMaterial } : {}),
        ...(frameSize ? { size: frameSize } : {}),
        framePriceTtc,
        highPrice,
        ...(hasLensOptions ? { offerCount: lensOptions.length + 1 } : {}),
        currency: country.currency,
        countryCode: country.code,
        inStock,
        stockQuantity,
        lang,
      }),
      breadcrumbNode([
        { name: countryName, url: `/${countrySlug}` },
        { name: lang === "fr" ? "Lunettes" : "Glasses", url: `/${countrySlug}/glasses` },
        { name: frame.name },
      ]),
      faqNode(faqItems),
    ];
  }, [frame, framePriceTtc, inStock, stockQuantity, lensOptions, country.currency, country.code, countrySlug, slug, countryName, lang, faqItems]);

  // Analytics: product_view (idle, post-LCP friendly)
  // IMPORTANT: declared BEFORE any early return to respect Rules of Hooks (prevents React error #310)
  useEffect(() => {
    if (!frame?.id) return;
    import("@/lib/analytics/track").then(({ trackIdle }) => {
      trackIdle("product_view", {
        product_id: frame.id,
        product_name: frame.name,
        product_slug: frame.slug,
        product_category: (frame as any).shape ?? undefined,
        frame_collection: (frame as any).collection ?? undefined,
        price: framePriceTtc || undefined,
        currency_code: country.currency,
        availability_status: inStock ? "in_stock" : "out_of_stock",
      });
    });
  }, [frame?.id, frame?.name, frame?.slug, framePriceTtc, inStock, country.currency]);

  // Loading state — keep the SAME outer wrapper + image aspect as the loaded layout
  // so there is zero CLS when frame data lands.
  if (fetching) {
    return (
      <div className="w-full mx-auto px-3 sm:px-4 pt-0 md:pt-6 pb-32 md:pb-6 md:max-w-5xl">
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          <div className="space-y-3 min-w-0">
            <div className="aspect-[3/2] max-h-[50vh] md:aspect-square md:max-h-none rounded-lg overflow-hidden bg-secondary/20">
              <Skeleton className="w-full h-full rounded-lg" />
            </div>
          </div>
          <div className="space-y-5 min-w-0">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-7 w-1/3" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (!frame) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">
          {lang === "fr" ? "Produit introuvable" : "Product not found"}
        </h1>
        <Button variant="outline" onClick={() => navigate(getLastListPath() || `/${countrySlug}/glasses`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t("product.backToGlasses")}
        </Button>
      </div>
    );
  }

  const backToListPath = getLastListPath() || `/${countrySlug}/glasses`;
  const handleBackToList = (e: React.MouseEvent) => {
    // If browser history allows, prefer back() so native scroll restoration via POP kicks in
    if (window.history.length > 1) {
      e.preventDefault();
      navigate(-1);
    }
  };


  // Per-page metadata (unique title + description per product)
  const productName = frame?.name ?? "";
  const pageTitle = productName
    ? (lang === "fr"
        ? `${productName} — Lunettes ${country.preposition} ${countryName} | Lapaire`
        : `${productName} — Glasses in ${countryName} | Lapaire`)
    : (lang === "fr" ? `Lunettes ${country.preposition} ${countryName} | Lapaire` : `Glasses in ${countryName} | Lapaire`);
  const pageDescription = productName
    ? (lang === "fr"
        ? `Achetez la monture ${productName} ${country.preposition} ${countryName}${priceDisplay ? (frame?.isSunglasses ? ` au prix de ${priceDisplay}` : ` à partir de ${priceDisplay}`) : ""}. Test de vue gratuit en magasin Lapaire.`
        : `Buy ${productName} frames in ${countryName}${priceDisplay ? (frame?.isSunglasses ? ` for ${priceDisplay}` : ` from ${priceDisplay}`) : ""}. Free eye test at Lapaire stores.`)
    : (lang === "fr" ? `Découvrez nos lunettes Lapaire ${country.preposition} ${countryName}.` : `Discover Lapaire glasses in ${countryName}.`);
  const canonicalUrl = `https://lapaire.africa/${countrySlug}/product/${slug}`;
  const ogImage = images[0]?.url;

  // ===== Commande en ligne =====
  const selectedLens = lensOptions.find((l) => l.id === selectedLensId) ?? null;
  const selectedLensRaw = selectedLens;
  const orderTotal: number | null = frame?.isSunglasses
    ? framePriceTtc
    : framePriceTtc !== null && selectedLens
      ? framePriceTtc + selectedLens.price_ttc
      : null;
  const canOrder = !!frame && inStock && orderTotal !== null && (frame.isSunglasses || !!selectedLens);
  const orderLabel = frame?.isSunglasses
    ? (lang === "fr" ? "Acheter" : "Buy")
    : (lang === "fr" ? "Commander" : "Order");
  const paymentReassurance = mobileMoneyEnabled
    ? (lang === "fr"
        ? "Paiement sécurisé · Carte bancaire · Mobile Money · Retrait en agence"
        : "Secure payment · Card · Mobile Money · Pick up in store")
    : (lang === "fr"
        ? "Paiement sécurisé par carte · Retrait en agence"
        : "Secure card payment · Pick up in store");
  const handleOrder = (source: string, lensOverride?: typeof selectedLens) => {
    if (!frame || !canOrder || framePriceTtc === null) return;
    if (!frame.isSunglasses && !lensOverride && source === "sticky_mobile") { setPickerOpen(true); return; }
    const selectedLens = lensOverride ?? selectedLensRaw;
    const lensPrice = frame.isSunglasses ? 0 : selectedLens!.price_ttc;
    addCartItem({
      frameId: frame.id,
      frameName: lang === "en" && frame.name_en ? frame.name_en : frame.name,
      frameSlug: frame.slug,
      frameImageUrl: frame.base_image_url,
      lensTypeId: frame.isSunglasses ? "" : selectedLens!.id,
      lensDisplayName: frame.isSunglasses ? "" : (lang === "en" ? selectedLens!.display_name_en : selectedLens!.display_name_fr),
      countryId: country.id,
      currencyCode: country.currency,
      framePriceTtc,
      lensPriceTtc: lensPrice,
      totalLineTtc: framePriceTtc + lensPrice,
    });
    import("@/lib/analytics/track").then(({ track }) => {
      track("product_order_cta", { frame_id: frame.id, sku: frame.sku, source, value: orderTotal });
    }).catch(() => {});
    navigate("/checkout");
  };
  const goFrameTest = () => {
    if (!frame) return;
    import("@/lib/analytics/track").then(({ track }) => {
      track("booking_frame_test_cta", { frame_id: frame.id, sku: frame.sku, slug: frame.slug, in_stock: inStock });
    });
    navigate(`/${countrySlug}/book?frame=${frame.id}&type=frame_test`);
  };


  return (
    <>
      <SchemaGraph nodes={schemaNodes} />
      <div className="w-full mx-auto px-3 sm:px-4 pt-0 md:pt-6 pb-32 md:pb-6 md:max-w-5xl">
        {/* Back link */}
        <div className="sticky top-14 z-40 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 mb-3 bg-background/95 backdrop-blur border-b border-border md:static md:mx-0 md:px-0 md:py-0 md:mb-4 md:bg-transparent md:backdrop-blur-0 md:border-0">
          <Link
            to={backToListPath}
            onClick={handleBackToList}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t("product.backToGlasses")}
          </Link>
        </div>

        {/* ===== 1. HERO ===== */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {/* Image gallery */}
          <div className="space-y-3 min-w-0">
            <div className="relative aspect-[3/2] max-h-[50vh] md:aspect-square md:max-h-none rounded-lg overflow-hidden bg-secondary/20">
              <FavoriteButton frameId={frame.id} countrySlug={country.slug} lang={lang} floating />
              {images[selectedImage] ? (
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={lang === "fr" ? "Agrandir l'image" : "Enlarge image"}
                  className="w-full h-full block cursor-zoom-in"
                >
                  <img
                    src={frameImageUrl(images[selectedImage].url, "detail")}
                    alt={images[selectedImage].alt}
                    className="w-full h-full object-contain mx-auto"
                    width={800}
                    height={800}
                    sizes="(min-width: 768px) 480px, 100vw"
                    fetchPriority="high"
                    loading="eager"
                    decoding="async"
                  />
                </button>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  {lang === "fr" ? "Aucune image" : "No image"}
                </div>
              )}
            </div>
            {lightboxOpen && (
              <Suspense fallback={null}>
                <ImageLightbox
                  images={images}
                  open={lightboxOpen}
                  initialIndex={selectedImage}
                  onOpenChange={setLightboxOpen}
                  onIndexChange={setSelectedImage}
                  lang={lang as "fr" | "en"}
                />
              </Suspense>
            )}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    aria-label={img.alt || `Image ${i + 1}`}
                    className={`w-20 h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      i === selectedImage ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img src={frameImageUrl(img.url, "thumb")} alt={img.alt} className="w-full h-full object-cover" width={80} height={80} loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="space-y-5 min-w-0">
            <div>
              {frame.isLapairePlus && (
                <Link
                  to={`/${countrySlug}/lapaire-plus`}
                  className="inline-flex items-center rounded-full bg-foreground px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-background mb-2"
                >
                  Lapaire Plus
                </Link>
              )}
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground break-words">{lang === "en" && frame.name_en ? frame.name_en : frame.name}</h1>
              <p className="text-2xl font-semibold text-primary mt-2">{priceDisplay}</p>
              {frame.collection_name && (
                <p className="text-sm text-muted-foreground mt-1">{frame.collection_name}</p>
              )}
            </div>

            {/* ===== Choix des verres ===== */}
            {lensOptions.length > 0 && framePriceTtc !== null && (
              <div className="space-y-2">
                <h2 className="text-sm font-semibold text-foreground">
                  {lang === "fr" ? "Choisis tes verres" : "Choose your lenses"}
                </h2>
                <div className="grid gap-2">
                  {lensOptions.map((lens) => {
                    const total = framePriceTtc + lens.price_ttc;
                    const active = lens.id === selectedLensId;
                    return (
                      <button
                        key={lens.id}
                        type="button"
                        onClick={() => setSelectedLensId(lens.id)}
                        aria-pressed={active}
                        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                          active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <span className="text-sm text-foreground min-w-0 break-words">
                          {lang === "en" ? lens.display_name_en : lens.display_name_fr}
                        </span>
                        <span className="text-sm font-semibold text-primary whitespace-nowrap">
                          {formatPrice(total)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {lang === "fr"
                    ? "Prix tout compris : monture + verres correcteurs."
                    : "All-inclusive price: frame + prescription lenses."}
                </p>
                <Button
                  size="lg"
                  className="w-full font-bold"
                  disabled={!canOrder}
                  onClick={() => handleOrder("lens_list")}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  {orderLabel}{orderTotal !== null ? ` · ${formatPrice(orderTotal)}` : ""}
                </Button>
                <p className="text-xs text-muted-foreground text-center">{paymentReassurance}</p>
              </div>
            )}

            {/* ===== Solaires : achat direct, sans verres ===== */}
            {frame.isSunglasses && framePriceTtc !== null && (
              <div className="space-y-2">
                <Button
                  size="lg"
                  className="w-full font-bold"
                  disabled={!canOrder}
                  onClick={() => handleOrder("sunglasses")}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  {orderLabel} · {formatPrice(framePriceTtc)}
                </Button>
                <p className="text-xs text-muted-foreground text-center">{paymentReassurance}</p>
              </div>
            )}



            {/* Badges */}
            <div className="flex flex-wrap gap-2 min-w-0">
              {inStock ? (
                <Badge variant="secondary" className="text-xs">
                  <Check className="h-3 w-3 mr-1" />
                  {replacePlaceholders(t("product.inStockAt"), { country: countryName, prep: country.preposition })}
                </Badge>
              ) : (
                <Badge variant="destructive" className="text-xs">{t("product.outOfStock")}</Badge>
              )}
              <Badge variant="outline" className="text-xs">
                <Store className="h-3 w-3 mr-1" />
                {t("product.availableInStore")}
              </Badge>
              {frame.gender && (
                <Badge variant="outline" className="text-xs capitalize">{frame.gender}</Badge>
              )}
              {(lang === "en" ? frame.shape_en || frame.shape : frame.shape) && (
                <Badge variant="outline" className="text-xs capitalize">{lang === "en" ? frame.shape_en || frame.shape : frame.shape}</Badge>
              )}
            </div>

            {/* ===== Pourquoi Lapaire Plus (montures premium uniquement) ===== */}
            {frame.isLapairePlus && (
              <section className="rounded-lg border border-foreground/15 bg-foreground/[0.04] p-4">
                <h2 className="text-sm font-semibold text-foreground">{plusCopy.productBox.title}</h2>
                <ul className="mt-2 space-y-1.5">
                  {plusCopy.productBox.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="min-w-0 break-words">{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/${countrySlug}/lapaire-plus`}
                  className="mt-3 inline-block text-sm font-medium text-primary underline"
                >
                  {plusCopy.productBox.link} →
                </Link>
              </section>
            )}

            {/* Trust block */}
            <div className="bg-secondary/30 rounded-lg p-3 space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-foreground min-w-0">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                <span>{t("product.trustMobileMoney")}</span>
              </div>
              <div className="flex items-center gap-2 text-foreground min-w-0">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                <span>{t("product.trustPickup")}</span>
              </div>
              <div className="flex items-center gap-2 text-foreground min-w-0">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                <span>{t("product.trustEyeTest")}</span>
              </div>
            </div>

            {/* ===== CTA: Try in store ===== */}
            <div className="hidden md:block border-t border-border pt-4">
              <Button
                size="lg"
                className="w-full font-bold"
                onClick={() => {
                  import("@/lib/analytics/track").then(({ track }) => {
                    track("booking_frame_test_cta", {
                      frame_id: frame.id,
                      sku: frame.sku,
                      slug: frame.slug,
                      in_stock: inStock,
                    });
                  });
                  navigate(`/${countrySlug}/book?frame=${frame.id}&type=frame_test`);
                }}
              >
                <Store className="h-4 w-4 mr-2" />
                {lang === "fr" ? "Teste ces lunettes en agence" : "Try these frames in store"}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                {inStock
                  ? lang === "fr"
                    ? "Gratuit · Sans engagement · Toutes nos agences"
                    : "Free · No commitment · All our stores"
                  : lang === "fr"
                    ? "Stock limité — l'agence vous confirmera la disponibilité"
                    : "Limited stock — the store will confirm availability"}
              </p>
            </div>

            {/* Out of stock notice */}
            {!inStock && (
              <p className="text-sm text-muted-foreground">
                {lang === "fr"
                  ? "ℹ️ Stock limité dans certaines agences — réservez quand même un essai, l'agence vous confirmera."
                  : "ℹ️ Limited stock in some stores — book a try-on anyway, the store will confirm."}
              </p>
            )}
          </div>
        </div>

        {/* ===== WHY LAPAIRE ===== */}
        <section className="mt-8 md:mt-12 space-y-4">
          <h2 className="text-lg md:text-xl font-bold text-foreground">
            {replacePlaceholders(t("product.whyTitle"), { country: countryName })}
          </h2>
          <ul className="space-y-2 text-sm text-foreground">
            {[
              t("product.whyMobileMoney"),
              t("product.whyPickup"),
              t("product.whyWhatsApp"),
              t("product.whyAdjustment"),
              t("product.whyNetwork"),
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* ===== DETAILS ===== */}
        {(() => {
          const desc = lang === "en" ? frame.description_en || frame.description : frame.description || frame.description_en;
          const material = lang === "en" ? frame.material_en || frame.material : frame.material || frame.material_en;
          const color = lang === "en" ? frame.color_en || frame.color : frame.color || frame.color_en;
          const size = lang === "en" ? frame.size_label_en || frame.size_label : frame.size_label || frame.size_label_en;
          if (!desc && !material && !color && !size) return null;
          return (
            <section className="mt-8 md:mt-12 space-y-3">
              <h2 className="text-lg md:text-xl font-bold text-foreground">{t("product.detailsTitle")}</h2>
              {desc && <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>}
              {(material || color || size) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {material && <Badge variant="outline" className="text-xs capitalize">{material}</Badge>}
                  {color && <Badge variant="outline" className="text-xs capitalize">{color}</Badge>}
                  {size && <Badge variant="outline" className="text-xs">{size}</Badge>}
                </div>
              )}
            </section>
          );
        })()}

        {/* ===== SIMILAR PRODUCTS ===== */}
        {similarProducts.length > 0 && (
          <section className="mt-8 md:mt-12 space-y-4">
            <h2 className="text-lg md:text-xl font-bold text-foreground">
              {lang === "fr" ? "Produits similaires" : "Similar products"}
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory -mx-3 px-3 sm:mx-0 sm:px-0">
              {similarProducts.map((p) => {
                const pName = lang === "en" && p.name_en ? p.name_en : p.name;
                return (
                  <Link
                    key={p.id}
                    to={`/${countrySlug}/product/${p.slug}`}
                    className="group flex-shrink-0 w-40 sm:w-48 snap-start rounded-lg border border-border hover:border-primary/40 transition-colors overflow-hidden bg-background"
                  >
                    <div className="aspect-[3/2] bg-secondary/20 overflow-hidden">
                      {p.base_image_url ? (
                        <img
                          src={frameImageUrl(p.base_image_url, "card")}
                          alt={pName}
                          loading="lazy"
                          decoding="async"
                          width={300}
                          height={200}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                          {lang === "fr" ? "Aucune image" : "No image"}
                        </div>
                      )}
                    </div>
                    <div className="p-2.5 space-y-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{pName}</p>
                      <p className="text-sm font-semibold text-primary">
                        {formatPrice(p.startingPrice)}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ===== LOCAL AVAILABILITY ===== */}
        {cities && cities.length > 0 && (
          <section className="mt-8 md:mt-12 space-y-4">
            <h2 className="text-lg md:text-xl font-bold text-foreground">
              {replacePlaceholders(t("product.availabilityTitle"), { country: countryName, prep: country.preposition })}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {cities.map((city) => (
                <Link
                  key={city.id}
                  to={`/${countrySlug}/${city.slug}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/30 transition-colors text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0 truncate">
                    <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="truncate">{replacePlaceholders(t("product.seeStoresIn"), { city: city.name })}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ===== HOW IT WORKS ===== */}
        <section className="mt-8 md:mt-12 space-y-4">
          <h2 className="text-lg md:text-xl font-bold text-foreground">{t("product.howTitle")}</h2>
          <ol className="space-y-3">
            {[t("product.howStep1"), t("product.howStep2"), t("product.howStep3")].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
                  {i + 1}
                </span>
                <span className="pt-1">{step}</span>
              </li>
            ))}
          </ol>
          <Link to={`/${countrySlug}/prices`} className="inline-flex items-center text-sm text-primary hover:underline">
            {replacePlaceholders(t("product.howPricesLink"), { country: countryName, prep: country.preposition })}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </section>

        {/* ===== FAQ ===== */}
        <section className="mt-8 md:mt-12 space-y-4">
          <h2 className="text-lg md:text-xl font-bold text-foreground">{t("product.faqTitle")}</h2>
          <div className="w-full">
            {faqItems.map((faq, i) => (
              <Disclosure
                key={i}
                summary={<span className="text-sm text-left break-words">{faq.q}</span>}
                contentClassName="text-muted-foreground"
              >
                {faq.a}
              </Disclosure>
            ))}
          </div>
        </section>

        {/* ===== INTERNAL LINKS ===== */}
        <section className="mt-8 md:mt-12 pb-4 space-y-2">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to={`/${countrySlug}/glasses`} className="text-primary hover:underline">{t("product.backToGlasses")}</Link>
            <Link
              to={`/${countrySlug}/book`}
              className="text-primary hover:underline"
              onClick={() => {
                import("@/lib/analytics/trackBookCta").then(({ trackBookEyeTestCta }) =>
                  trackBookEyeTestCta("product_inline_link", `/${countrySlug}/book`, "secondary")
                );
              }}
            >{t("product.bookEyeTest")}</Link>
            <Link to={`/${countrySlug}/stores`} className="text-primary hover:underline">{t("nav.stores")}</Link>
          </div>
        </section>
      </div>

      {/* ===== STICKY MOBILE CTA ===== */}
      <div className="fixed bottom-16 left-0 right-0 z-40 md:hidden bg-background border-t border-border px-3 py-2">
        <div className={canOrder ? "grid grid-cols-[2fr_3fr] gap-2" : ""}>
          <Button
            size="lg"
            variant={canOrder ? "outline" : "default"}
            className="w-full min-w-0 font-bold px-2 text-sm"
            onClick={goFrameTest}
          >
            {!canOrder && <Store className="h-4 w-4 mr-1.5 shrink-0" />}
            <span className="truncate">
              {canOrder
                ? (lang === "fr" ? "Tester en agence" : "Try in store")
                : (lang === "fr" ? "Teste ces lunettes en agence" : "Try these frames in store")}
            </span>
          </Button>
          {canOrder && (
            <Button
              size="lg"
              className="w-full min-w-0 font-bold px-2 text-sm"
              onClick={() => handleOrder("sticky_mobile")}
            >
              <span className="truncate">
                {orderLabel} · {formatPrice(orderTotal!)}
              </span>
            </Button>
          )}
        </div>
      </div>
      {frame && !frame.isSunglasses && framePriceTtc !== null && (
        <LensPickerSheet
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          lang={lang}
          currency={country.currency}
          framePriceTtc={framePriceTtc}
          options={lensOptions}
          initialLensId={selectedLensId}
          confirmLabel={orderLabel}
          onConfirm={(l) => {
            setSelectedLensId(l.id);
            handleOrder("sticky_mobile_picker", lensOptions.find((o) => o.id === l.id) ?? null);
          }}
        />
      )}
    </>
  );
}
